<template>
  <div class="user-withdrawal-management">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <div class="filter-card">
        <el-row :gutter="20">
          <el-col :span="22">
            <div style="display: flex; align-items: center; gap: 10px;">
              <el-input
                v-model="username"
                placeholder="搜索用户名"
                prefix-icon="el-icon-user"
                clearable
                @keyup.enter.native="handleSearch"
                style="width: 180px;"
              ></el-input>
              <el-input
                v-model="alipayAccount"
                placeholder="搜索支付宝账号"
                prefix-icon="el-icon-message"
                clearable
                @keyup.enter.native="handleSearch"
                style="width: 200px;"
              ></el-input>
              <el-date-picker
                v-model="selectedDate"
                type="date"
                placeholder="选择日期（之后）"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                clearable
                @change="handleDateChange"
                style="width: 190px;"
              ></el-date-picker>
              <el-button
                type="primary"
                icon="el-icon-search"
                @click="handleSearch"
              >查询</el-button>
              <el-button
                icon="el-icon-refresh"
                @click="refreshList"
              >重置</el-button>
              <el-button
                type="success"
                icon="el-icon-download"
                :loading="exportLoading"
                @click="handleExport"
              >导出Excel</el-button>
            </div>
          </el-col>
        </el-row>
      </div>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty
        v-else-if="!loading && records.length === 0"
        description="暂无提现记录"
      />

      <div v-else>
        <!-- 提现记录表格 -->
        <el-table
          :data="records"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载提现记录..."
          class="animate__animated animate__fadeIn"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="username" label="用户名" min-width="120" show-overflow-tooltip />
          <el-table-column prop="alipayAccount" label="支付宝账号" min-width="180" show-overflow-tooltip />
          <el-table-column label="提现金额" width="120" align="center">
            <template slot-scope="scope">
              <span style="color: #409EFF; font-weight: bold;">¥{{ scope.row.withdrawalAmount }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="withdrawalTime" label="提现时间" width="180" align="center">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.withdrawalTime) }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status === 0" type="warning">待审核</el-tag>
              <el-tag v-else-if="scope.row.status === 1" type="success">审核成功</el-tag>
              <el-tag v-else-if="scope.row.status === 2" type="danger">审核失败</el-tag>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="220" align="center">
            <template slot-scope="scope">
              <div v-if="scope.row.status === 0" style="display: flex; gap: 8px; justify-content: center;">
                <el-button
                  size="mini"
                  type="success"
                  icon="el-icon-check"
                  @click="handleApprove(scope.row)"
                >通过</el-button>
                <el-button
                  size="mini"
                  type="danger"
                  icon="el-icon-close"
                  @click="handleReject(scope.row)"
                >拒绝</el-button>
              </div>
              <span v-else style="color: #909399; font-size: 12px;">已处理</span>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.pageNum"
            :page-size="pagination.pageSize"
            :page-sizes="[5, 10, 20, 50]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 审核通过确认对话框 -->
    <el-dialog
      title="审核通过"
      :visible.sync="approveDialogVisible"
      width="400px"
      center
    >
      <p>确定要通过用户 <strong>{{ currentRecord.username }}</strong> 的提现申请吗？</p>
      <p>提现金额：<strong style="color: #409EFF;">¥{{ currentRecord.withdrawalAmount }}</strong></p>
      <p>支付宝账号：<strong>{{ currentRecord.alipayAccount }}</strong></p>
      <div slot="footer" class="dialog-footer">
        <el-button @click="approveDialogVisible = false">取 消</el-button>
        <el-button type="success" @click="confirmApprove">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 审核拒绝确认对话框 -->
    <el-dialog
      title="审核拒绝"
      :visible.sync="rejectDialogVisible"
      width="400px"
      center
    >
      <p>确定要拒绝用户 <strong>{{ currentRecord.username }}</strong> 的提现申请吗？</p>
      <p>提现金额：<strong style="color: #409EFF;">¥{{ currentRecord.withdrawalAmount }}</strong></p>
      <p>支付宝账号：<strong>{{ currentRecord.alipayAccount }}</strong></p>
      <div slot="footer" class="dialog-footer">
        <el-button @click="rejectDialogVisible = false">取 消</el-button>
        <el-button type="danger" @click="confirmReject">确 定拒绝</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { get, put } from '@/utils/http'
import * as XLSX from 'xlsx'
import FileSaver from 'file-saver'

export default {
  name: 'UserWithdrawalManagement',
  data() {
    return {
      records: [],
      loading: false,
      exportLoading: false,
      error: '',
      username: '',
      alipayAccount: '',
      selectedDate: '',
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      approveDialogVisible: false,
      rejectDialogVisible: false,
      currentRecord: {}
    }
  },
  async mounted() {
    // 默认不设置日期，直接查询全部
    await this.fetchRecords()
  },
  methods: {
    async fetchRecords() {
      this.loading = true
      this.error = ''
      
      try {
        const params = {
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        }
        
        // 添加用户名参数
        if (this.username) {
          params.username = this.username
        }
        
        // 添加支付宝账号参数
        if (this.alipayAccount) {
          params.alipayAccount = this.alipayAccount
        }
        
        // 添加日期参数
        if (this.selectedDate) {
          params.date = this.selectedDate
        }
        
        const response = await get('/admin/userWithdrawalRecords/getUserWithdrawalRecordsList', params)
        
        // 处理返回的记录，确保 status 字段存在（默认为 0-待审核）
        this.records = (response.data.records || []).map(record => ({
          ...record,
          status: record.status !== undefined ? record.status : 0
        }))
        this.pagination.total = response.data.total || 0
        
      } catch (error) {
        console.error('获取提现记录失败:', error)
        this.error = error.message || '获取提现记录失败'
        this.$message.error(this.error)
      } finally {
        this.loading = false
      }
    },
    
    handleCurrentChange(pageNum) {
      this.pagination.pageNum = pageNum
      this.fetchRecords()
    },
    
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize
      this.pagination.pageNum = 1
      this.fetchRecords()
    },
    
    handleDateChange() {
      // 日期改变时重置到第一页
      this.pagination.pageNum = 1
    },
    
    handleSearch() {
      this.pagination.pageNum = 1
      this.fetchRecords()
    },
    
    refreshList() {
      this.username = ''
      this.alipayAccount = ''
      this.selectedDate = ''
      this.pagination.pageNum = 1
      this.fetchRecords()
    },

    async handleExport() {
      this.exportLoading = true
      try {
        const allRecords = []
        let pageNum = 1
        const pageSize = 50 // 每次获取50条，避免一次请求过多
        let hasMore = true

        // 第一次请求获取总数，也可以直接开始循环
        // 这里直接开始循环，直到没有数据
        while (hasMore) {
          const params = {
            pageNum: pageNum,
            pageSize: pageSize
          }
          // 添加用户名参数
          if (this.username) {
            params.username = this.username
          }
          // 添加支付宝账号参数
          if (this.alipayAccount) {
            params.alipayAccount = this.alipayAccount
          }
          if (this.selectedDate) {
            params.date = this.selectedDate
          }

          const response = await get('/admin/userWithdrawalRecords/getUserWithdrawalRecordsList', params)
          const records = response.data.records || []
          
          if (records.length > 0) {
            allRecords.push(...records)
            
            // 如果获取的数量小于pageSize，说明没有更多数据了
            if (records.length < pageSize) {
              hasMore = false
            } else {
              pageNum++
            }
          } else {
            hasMore = false
          }
          
          // 如果后端返回了总数，也可以用总数来判断是否结束，作为双重保险
          if (response.data.total && allRecords.length >= response.data.total) {
            hasMore = false
          }
        }

        if (allRecords.length === 0) {
          this.$message.warning('当前条件下没有可导出的数据')
          return
        }

        // 格式化数据
        const exportData = allRecords.map(record => {
          let statusText = '-'
          if (record.status === 0) statusText = '待审核'
          else if (record.status === 1) statusText = '审核成功'
          else if (record.status === 2) statusText = '审核失败'

          return {
            'ID': record.id,
            '用户名': record.username,
            '支付宝账号': record.alipayAccount,
            '提现金额': record.withdrawalAmount,
            '提现时间': this.formatDateTime(record.withdrawalTime),
            '状态': statusText
          }
        })

        // 创建工作簿
        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(exportData)
        
        // 设置列宽
        const wscols = [
          { wch: 10 }, // ID
          { wch: 20 }, // 用户名
          { wch: 25 }, // 支付宝账号
          { wch: 15 }, // 提现金额
          { wch: 20 }, // 提现时间
          { wch: 10 }  // 状态
        ]
        ws['!cols'] = wscols

        XLSX.utils.book_append_sheet(wb, ws, '提现记录')

        // 导出文件
        const searchInfo = []
        if (this.username) searchInfo.push(`用户_${this.username}`)
        if (this.alipayAccount) searchInfo.push(`支付宝_${this.alipayAccount}`)
        if (this.selectedDate) searchInfo.push(`日期_${this.selectedDate}`)
        const fileName = `提现记录_${searchInfo.length > 0 ? searchInfo.join('_') : '全部'}_${new Date().getTime()}.xlsx`
        const wbout = XLSX.write(wb, { bookType: 'xlsx', bookSST: true, type: 'array' })
        
        FileSaver.saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName)
        
        this.$message.success(`成功导出 ${allRecords.length} 条记录`)

      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败: ' + (error.message || '未知错误'))
      } finally {
        this.exportLoading = false
      }
    },
    
    handleApprove(record) {
      this.currentRecord = record
      this.approveDialogVisible = true
    },
    
    handleReject(record) {
      this.currentRecord = record
      this.rejectDialogVisible = true
    },
    
    async confirmApprove() {
      try {
        await put('/admin/userWithdrawalRecords', {
          id: String(this.currentRecord.id),
          status: 1
        })
        
        this.$message.success('审核通过成功')
        this.approveDialogVisible = false
        this.fetchRecords() // 刷新列表
      } catch (error) {
        this.$message.error(error.message || '审核失败')
      }
    },
    
    async confirmReject() {
      try {
        await put('/admin/userWithdrawalRecords', {
          id: String(this.currentRecord.id),
          status: 2
        })
        
        this.$message.success('审核拒绝成功')
        this.rejectDialogVisible = false
        this.fetchRecords() // 刷新列表
      } catch (error) {
        this.$message.error(error.message || '审核失败')
      }
    },
    
    formatDateTime(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.user-withdrawal-management {
}

.filter-card {
  margin-bottom: 20px;
}

.error {
  color: #f87171;
  padding: 10px;
  background-color: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 4px;
  margin-bottom: 20px;
}

.el-table {
  margin-bottom: 20px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
}

.dialog-footer {
  text-align: right;
}
</style>
